import { useState } from 'react';
import { Stack } from '@mui/material';
import { useTranslate } from 'react-admin';
import { useNavigate } from 'react-router-dom';
import type { AdminApplication } from '@/api/models';
import { PageTitle } from '@/components/shell/PageTitle';
import { SectionHeader } from '@/components/shell/SectionHeader';
import { useCanWrite } from '@/access/useCanWrite';
import { ApplicationsListView } from './ApplicationsListView';
import { ApplicationCreateDialog } from './ApplicationCreateDialog';

/**
 * Applications surface against `/v1/admin/applications` (A3's contract).
 * The contract only defines `page`/`pageSize` on the list — no filters, so
 * this screen has no `FiltersPopover`. `platform:auditor` sees the same
 * list read-only: the Create button is hidden via `useCanWrite`.
 */
export function ApplicationsPage() {
  const translate = useTranslate();
  const navigate = useNavigate();
  const canWrite = useCanWrite();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [refreshToken, setRefreshToken] = useState(0);
  const [createOpen, setCreateOpen] = useState(false);

  function handleCreated(application: AdminApplication) {
    setCreateOpen(false);
    setRefreshToken((token) => token + 1);
    navigate(`/applications/${encodeURIComponent(application.code)}`);
  }

  return (
    <Stack spacing={1.5}>
      <SectionHeader
        left={<PageTitle title={translate('pay.applications.pageTitle')} subtitle={translate('pay.applications.pageSubtitle')} />}
        onRefresh={() => setRefreshToken((token) => token + 1)}
        onCreate={canWrite ? () => setCreateOpen(true) : undefined}
        createLabel={translate('pay.applications.list.createLabel')}
      />
      <ApplicationsListView
        key={refreshToken}
        page={page}
        pageSize={pageSize}
        onPageChange={(nextPage, nextPageSize) => {
          setPage(nextPage);
          setPageSize(nextPageSize);
        }}
      />
      <ApplicationCreateDialog open={createOpen} onClose={() => setCreateOpen(false)} onCreated={handleCreated} />
    </Stack>
  );
}
